import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, CalendarDays, Users, Package, Calendar } from "lucide-react";
import { adminGetOrders, adminGetBookings, adminGetSubscribers, adminGetProducts, adminGetPayments, API } from "@/lib/api";
import axios from "axios";

export const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#1F3864]/10 p-5 flex items-center gap-4">
    <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#1F3864] text-[#D7E85C]">
      <Icon size={20} />
    </div>
    <div>
      <div className="text-2xl font-display font-bold text-[#1F3864]">{value}</div>
      <div className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">{label}</div>
    </div>
  </div>
);

export default function Overview() {
  const [stats, setStats] = useState({ orders: "—", bookings: "—", subscribers: "—", products: "—" });
  const [recent, setRecent] = useState([]);
  const [gcal, setGcal] = useState({ connected: false, email: null });

  useEffect(() => {
    Promise.all([adminGetOrders(), adminGetBookings(), adminGetSubscribers(), adminGetProducts()])
      .then(([o, b, s, p]) => {
        setStats({ orders: o.length, bookings: b.length, subscribers: s.length, products: p.length });
        setRecent(o.slice(0, 5));
      })
      .catch(() => {});
    axios.get(`${API}/oauth/calendar/status`, { withCredentials: true, headers: localStorage.getItem("rada_session") ? { Authorization: `Bearer ${localStorage.getItem("rada_session")}` } : {} })
      .then((r) => setGcal(r.data)).catch(() => {});
  }, []);

  const connectGcal = async () => {
    try {
      const { data } = await axios.get(`${API}/oauth/calendar/connect`, { withCredentials: true, headers: localStorage.getItem("rada_session") ? { Authorization: `Bearer ${localStorage.getItem("rada_session")}` } : {} });
      window.location.href = data.url;
    } catch {
      alert("Google Calendar is not configured yet.");
    }
  };

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Overview</h1>
      <p className="text-[#1F3864]/60 mb-8">Your store at a glance.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={ShoppingCart} label="Orders" value={stats.orders} testid="stat-orders" />
        <StatCard icon={CalendarDays} label="Bookings" value={stats.bookings} testid="stat-bookings" />
        <StatCard icon={Users} label="Subscribers" value={stats.subscribers} testid="stat-subscribers" />
        <StatCard icon={Package} label="Products" value={stats.products} testid="stat-products" />
      </div>

      <div data-testid="gcal-card" className="bg-white rounded-2xl border border-[#1F3864]/10 p-5 mb-10 flex flex-wrap items-center gap-4">
        <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#1F3864] text-[#D7E85C]">
          <Calendar size={20} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-bold text-[#1F3864]">Booking calendar</div>
          <div className="text-xs text-[#1F3864]/50">
            {gcal.connected ? `Linked to ${gcal.email} — bookings are added automatically.` : "Link your Google Calendar to auto-add bookings and block busy times."}
          </div>
        </div>
        {gcal.connected ? (
          <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#D7E85C]/30 text-[#5C6B12]" data-testid="gcal-connected">Connected</span>
        ) : (
          <button data-testid="gcal-connect-btn" onClick={connectGcal}
            className="bg-[#1F3864] text-[#D7E85C] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#16294B]">
            Connect Google Calendar
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#1F3864]/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-[#1F3864]">Recent orders</h2>
          <Link to="/admin/orders" className="text-sm font-bold text-[#FF8473] hover:underline">View all</Link>
        </div>
        {recent.length === 0 ? (
          <p className="text-[#1F3864]/50 text-sm py-6 text-center" data-testid="recent-orders-empty">No orders yet.</p>
        ) : (
          <div className="divide-y divide-[#1F3864]/10">
            {recent.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0">
                  <div className="font-bold text-[#1F3864] truncate">{o.package_name || o.id}</div>
                  <div className="text-[#1F3864]/50 text-xs">{o.customer_email || "—"}</div>
                </div>
                <span className="shrink-0 px-3 py-1 rounded-full text-xs font-bold bg-[#1F3864]/10 text-[#1F3864]">
                  {o.status_label || o.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
